import { Fragment, useEffect, useRef } from 'react';

export interface BubbleMessage {
  id: string;
  content: string;
  status?: 'sent' | 'delivered' | 'read';
  isMine: boolean;
  senderName?: string;
  senderAvatar?: string;
  createdAt: string;
}

interface Props {
  messages: BubbleMessage[];
  isLoading?: boolean;
  emptyTitle?: string;
  emptySubtitle?: string;
  typingLabel?: string | null;
  renderAttachments?: (messageId: string) => React.ReactNode;
  /** When set, avatars that have a real image become clickable (e.g. to open a lightbox). */
  onAvatarClick?: (src: string, name?: string) => void;
}

const GROUP_GAP_MS = 5 * 60 * 1000; // same sender within 5 min = one group

const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });

const fmtDay = (iso: string) => {
  const d = new Date(iso);
  const now = new Date();
  const yest = new Date(now);
  yest.setDate(now.getDate() - 1);
  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (sameDay(d, now)) return 'Oggi';
  if (sameDay(d, yest)) return 'Ieri';
  return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
};

const initials = (name?: string) =>
  (name ?? '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('') || '?';

// Stable per-sender accent so different agents are visually distinct in a shared inbox.
const ACCENTS = [
  { ring: 'ring-brand-navy/25', text: 'text-brand-navy', bg: 'bg-brand-navy' },
  { ring: 'ring-brand-teal-600/30', text: 'text-brand-teal-600', bg: 'bg-brand-teal-600' },
  { ring: 'ring-brand-orange/30', text: 'text-brand-orange', bg: 'bg-brand-orange' },
  { ring: 'ring-fuchsia-500/30', text: 'text-fuchsia-600', bg: 'bg-fuchsia-500' },
];
const accentFor = (name?: string) => {
  if (!name) return ACCENTS[0]!;
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return ACCENTS[Math.abs(h) % ACCENTS.length]!;
};

function SingleCheck() {
  return (
    <svg viewBox="0 0 16 11" className="h-3 w-3.5" fill="none" aria-hidden>
      <path d="M1 6l3.2 3.2L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function DoubleCheck() {
  return (
    <svg viewBox="0 0 20 11" className="h-3 w-4" fill="none" aria-hidden>
      <path d="M1 6l3.1 3.1L10.5 1.7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6.5 6.1l3.1 3.1L18 1.7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function StatusTicks({ status }: { status?: BubbleMessage['status'] }) {
  if (!status) return null;
  if (status === 'sent') return <span className="text-white/55"><SingleCheck /></span>;
  if (status === 'delivered') return <span className="text-white/55"><DoubleCheck /></span>;
  return <span className="text-sky-200"><DoubleCheck /></span>; // read = brighter
}

function Avatar({ name, avatar, accent, onClick }: { name?: string; avatar?: string; accent: typeof ACCENTS[number]; onClick?: (src: string, name?: string) => void }) {
  if (avatar) {
    const img = <img src={avatar} alt={name ?? ''} className={`h-7 w-7 rounded-full object-cover ring-2 ${accent.ring}`} />;
    if (onClick) {
      return (
        <button
          type="button"
          onClick={() => onClick(avatar, name)}
          aria-label={`Apri foto di ${name ?? 'profilo'}`}
          className="rounded-full transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal"
        >
          {img}
        </button>
      );
    }
    return img;
  }
  return (
    <div className={`flex h-7 w-7 items-center justify-center rounded-full ${accent.bg} text-[10px] font-semibold text-white`}>
      {initials(name)}
    </div>
  );
}

export function MessageBubbleList({
  messages, isLoading, emptyTitle, emptySubtitle, typingLabel, renderAttachments, onAvatarClick,
}: Props) {
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length, typingLabel]);

  if (isLoading) {
    return <div className="flex flex-1 items-center justify-center text-sm text-neutral-400">Caricamento…</div>;
  }
  if (messages.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center bg-neutral-50/40 px-6 text-center">
        <p className="text-sm font-semibold text-neutral-700">{emptyTitle ?? 'Nessun messaggio'}</p>
        {emptySubtitle && <p className="mt-1 text-xs text-neutral-400">{emptySubtitle}</p>}
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-y-auto bg-neutral-50/40 px-4 py-4 sm:px-6">
      {messages.map((m, i) => {
        const prev = messages[i - 1];
        const next = messages[i + 1];
        const newDay = !prev || new Date(prev.createdAt).toDateString() !== new Date(m.createdAt).toDateString();
        const sameAsPrev = !!prev && prev.isMine === m.isMine && prev.senderName === m.senderName
          && !newDay && new Date(m.createdAt).getTime() - new Date(prev.createdAt).getTime() < GROUP_GAP_MS;
        const sameAsNext = !!next && next.isMine === m.isMine && next.senderName === m.senderName
          && new Date(next.createdAt).toDateString() === new Date(m.createdAt).toDateString()
          && new Date(next.createdAt).getTime() - new Date(m.createdAt).getTime() < GROUP_GAP_MS;
        const firstOfGroup = !sameAsPrev;
        const lastOfGroup = !sameAsNext;
        const accent = accentFor(m.senderName);

        return (
          <Fragment key={m.id}>
            {newDay && (
              <div className="my-3 flex items-center justify-center">
                <span className="rounded-full bg-neutral-200/70 px-3 py-1 text-[11px] font-medium text-neutral-500">
                  {fmtDay(m.createdAt)}
                </span>
              </div>
            )}

            <div className={`flex items-end gap-2 ${m.isMine ? 'flex-row-reverse' : 'flex-row'} ${firstOfGroup ? 'mt-2.5' : 'mt-0.5'}`}>
              {/* avatar gutter — rendered only on the last bubble of a group, when we have an identity */}
              <div className="w-7 shrink-0">
                {lastOfGroup && (m.senderAvatar || m.senderName) && (
                  <Avatar name={m.senderName} avatar={m.senderAvatar} accent={accent} onClick={onAvatarClick} />
                )}
              </div>

              <div className={`flex max-w-[78%] flex-col ${m.isMine ? 'items-end' : 'items-start'}`}>
                {firstOfGroup && m.senderName && (
                  <span className={`mb-0.5 px-1 text-[11.5px] font-semibold ${accent.text}`}>{m.senderName}</span>
                )}

                <div
                  className={[
                    'relative px-3.5 py-2 text-[14.5px] leading-snug shadow-sm rounded-2xl',
                    m.isMine
                      ? `bg-brand-teal text-white shadow-brand-teal/20 ${lastOfGroup ? 'rounded-br-md' : ''}`
                      : `border border-neutral-200/80 bg-white text-neutral-900 ${lastOfGroup ? 'rounded-bl-md' : ''}`,
                  ].join(' ')}
                >
                  {m.content && <p className="whitespace-pre-wrap break-words">{m.content}</p>}
                  {renderAttachments?.(m.id)}

                  {lastOfGroup && (
                    <div className={`mt-1 flex items-center justify-end gap-1 text-[10.5px] ${m.isMine ? 'text-white/65' : 'text-neutral-400'}`}>
                      <span>{fmtTime(m.createdAt)}</span>
                      {m.isMine && <StatusTicks status={m.status} />}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Fragment>
        );
      })}

      {typingLabel && (
        <div className="mt-3 flex items-end gap-2">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-neutral-200 text-[10px] font-semibold text-neutral-500">…</div>
          <div className="flex items-center gap-1 rounded-2xl rounded-bl-md border border-neutral-200/80 bg-white px-3.5 py-3 shadow-sm">
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-neutral-400 [animation-delay:-0.2s]" />
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-neutral-400 [animation-delay:-0.1s]" />
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-neutral-400" />
          </div>
        </div>
      )}
      <div ref={endRef} />
    </div>
  );
}
